// 03/11/2026 (registre n° 38) : vue d'ensemble de la charge dans la grille de l'onglet Planification.
// Ajout à côté de PlanningGrid.js : une ligne compacte par intervenant, pastilles = code projet (regroupées ×N),
// fond de case = charge du jour (hors astreinte et absences), hachures = absent, ligne « Charge équipe ».
// La vue « Détail » reste la grille d'origine. Lecture seule : rien n'est écrit dans SOPlanning ici.
import React from "react";
import "./PlanningOverview.css";

const MODE_KEY = "atis_pg_mode";
export const readMode = () => {
  try { const m = window.localStorage.getItem(MODE_KEY); if (m === "detail" || m === "overview") return m; } catch (e) { /* stockage indisponible */ }
  return "overview";
};
export const saveMode = (m) => {
  try { window.localStorage.setItem(MODE_KEY, m); } catch (e) { /* stockage indisponible */ }
};

const norm = (p) => String(p == null ? "" : p).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
// codes projet SOPlanning (projet_id) : absences et tâches hors charge
const ABSENT = { ferie: 1, conges: 1, ecole: 1, abs: 1, arret: 1 };
const HORS_CHARGE = { astreinte: 1 };
export const isAbsent = (pid) => !!ABSENT[norm(pid)];
export const enCharge = (pid) => !ABSENT[norm(pid)] && !HORS_CHARGE[norm(pid)];

export const ovCols = (ds) => "112px " + ds.map((d) => ((d.day() + 6) % 7 > 4 ? "44px" : "92px")).join(" ");

const hex6 = (c) => {
  const h = String(c == null ? "" : c).replace("#", "").trim();
  return /^[0-9a-fA-F]{6}$/.test(h) ? h.toLowerCase() : null;
};
export const textOn = (c) => {
  const h = hex6(c);
  if (!h) return "#fff";
  const y = (0.299 * parseInt(h.slice(0, 2), 16) + 0.587 * parseInt(h.slice(2, 4), 16) + 0.114 * parseInt(h.slice(4, 6), 16)) / 255;
  return y > 0.6 ? "#1d2733" : "#fff";
};
// couleur de l'intervenant dans SOPlanning (blanc = couleur de la grille d'origine)
export const userColor = (u, fallback) => {
  const h = hex6(u && u.color);
  return h && h !== "ffffff" ? "#" + h : fallback;
};
export const projColor = (projects, pid) => {
  const p = (projects || []).find((x) => String(x.value) === String(pid));
  const h = hex6(p && p.color);
  return h ? "#" + h : null;
};

export const ovGroups = (list) => {
  const m = {}, order = [];
  list.forEach((t) => {
    const k = String(t.project_id == null ? "" : t.project_id);
    if (!m[k]) { m[k] = { pid: k, n: 0, tasks: [] }; order.push(m[k]); }
    m[k].n++; m[k].tasks.push(t);
  });
  return order.sort((a, b) => b.n - a.n || a.pid.localeCompare(b.pid));
};
export const ovLoad = (list) => list.filter((t) => enCharge(t.project_id)).length;
export const ovCellClass = (list) => {
  if (list.some((t) => isAbsent(t.project_id))) return " ov-abs";
  const n = ovLoad(list);
  return n >= 7 ? " ov-l3" : n >= 4 ? " ov-l2" : n >= 1 ? " ov-l1" : "";
};

// contenu d'une case : opts = { pName(pid), titre(t), pColor(pid), onPick(e) }
export const ovCell = (list, opts) => {
  const groups = ovGroups(list), n = ovLoad(list), abs = list.some((t) => isAbsent(t.project_id));
  const MAX = 3;
  const shown = groups.length > MAX ? groups.slice(0, MAX - 1) : groups;
  const rest = groups.slice(shown.length);
  const restN = rest.reduce((s, g) => s + g.n, 0);
  const tip = (gs) => gs.map((g) => opts.pName(g.pid) + (g.n > 1 ? " ×" + g.n : "") + " :\n" +
    g.tasks.slice(0, 4).map((t) => "• " + opts.titre(t)).join("\n") + (g.tasks.length > 4 ? "\n• …" : "")).join("\n");
  return (
    <React.Fragment>
      {n >= 4 && !abs ? <span className="ov-n">{n} tâches</span> : null}
      {shown.map((g) => {
        const c = opts.pColor(g.pid) || "#9aa5b1";
        return (
          <span key={g.pid} className={"ov-p" + (c === "#ffffff" ? " ov-w" : "")} style={{ background: c, color: textOn(c) }}
            title={tip([g])} onClick={opts.onPick}>
            {g.pid.slice(0, 6) + (g.n > 1 ? " ×" + g.n : "")}
          </span>
        );
      })}
      {restN ? (
        <span className="ov-p ov-more" title={tip(rest)} onClick={opts.onPick}>{"+" + restN + " autre" + (restN > 1 ? "s" : "")}</span>
      ) : null}
    </React.Fragment>
  );
};

// ligne « Charge équipe » : nombre de tâches du jour des intervenants affichés, hors compte admin (hors astreinte et absences)
export const ovTotals = (ids, tasks, ds, uid, iso) => {
  const set = {};
  ids.forEach((i) => { if (i !== "ADM") set[i] = true; }); // compte « admin » (non visible dans SOPlanning) exclu
  const vals = ds.map((d) => {
    const s = iso(d);
    return tasks.filter((t) => t.start_date && set[uid(t.user_id)] && t.start_date <= s && (t.end_date || t.start_date) >= s && enCharge(t.project_id)).length;
  });
  const max = Math.max.apply(null, vals.concat([1]));
  const out = [<div key="ovtk" className="ov-tk">Charge équipe</div>];
  ds.forEach((d, i) => {
    const w = (d.day() + 6) % 7, pct = Math.round((vals[i] / max) * 100);
    out.push(
      <div key={"ovt" + iso(d)} className={"ov-t" + (w > 4 ? " we" : "") + (w === 0 && i > 0 ? " mon" : "")}
        style={{ backgroundImage: "linear-gradient(to top, rgba(11,115,160,.18) " + pct + "%, transparent " + pct + "%)" }}
        title={vals[i] + " tâche" + (vals[i] > 1 ? "s" : "") + " (hors astreinte et absences)"}>
        {w > 4 && !vals[i] ? "" : vals[i]}
      </div>
    );
  });
  return out;
};

export const ovLegend = (readOnly) => (
  <div className="ov-leg">
    <b>Charge du jour :</b>
    <span><i style={{ background: "#fff" }} />libre</span>
    <span><i style={{ background: "#EEF5FC" }} />1 à 3</span>
    <span><i style={{ background: "#FFF0CF" }} />4 à 6</span>
    <span><i style={{ background: "#FFD9A8" }} />7 et plus</span>
    <span><i className="ov-hach" />absent (férié, congés, école)</span>
    <span className="ov-leg-r">Astreinte affichée, non comptée.{readOnly ? "" : " Cliquer une case remplit Intervenant et Début ; Maj + clic ajoute ou retire un autre intervenant ; survoler une pastille pour le détail."}</span>
  </div>
);



// WEBPACK FOOTER //
// ./src/containers/Request/Tab/planningOverview.js